import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Logo } from "../../components/Logo";
import { Reveal } from "../../components/Reveal";
import { useLang } from "../../lib/i18n";
import { SERVICES, SERVICE_ORDER, OVERVIEW } from "./data";

export default function DienstenOverview() {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const o = OVERVIEW[lang];

  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta?.getAttribute("content");
    document.title = o.meta.title;
    if (meta) meta.setAttribute("content", o.meta.description);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [lang]);

  return (
    <div className="min-h-screen bg-ink" data-testid="diensten-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="diensten-lang-toggle">
              {["nl", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`diensten-lang-${code}`}
                  className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    lang === code ? "bg-amber font-bold text-[#181510]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber" data-testid="diensten-back-link">
              <ArrowLeft size={13} />
              {lang === "nl" ? "Terug naar de site" : "Back to the site"}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-14 md:py-20">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-faint" data-testid="diensten-eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-amber" aria-hidden="true" />
            {o.eyebrow}
          </p>
          <h1 className="mt-5 max-w-3xl font-archivo text-4xl font-black leading-[1.05] tracking-tight text-cream sm:text-5xl" data-testid="diensten-title">
            {o.title}
          </h1>
          <p className="mt-6 max-w-2xl leading-relaxed text-tan">{o.intro}</p>
        </Reveal>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {SERVICE_ORDER.map((slug, i) => {
            const s = SERVICES[slug][lang];
            return (
              <Reveal key={slug} delay={0.08 * i}>
                <Link
                  to={`/diensten/${slug}`}
                  className="group flex h-full flex-col rounded-[12px] border border-black/10 border-l-4 border-l-amber bg-card p-7 shadow-xl shadow-black/30 transition-colors duration-500 hover:border-amber/60"
                  data-testid={`diensten-card-${i}`}
                >
                  <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-faint transition-colors duration-500 group-hover:text-amberdeep">{s.num}</p>
                  <h2 className="mt-3 font-archivo text-xl font-extrabold tracking-tight text-cream">{s.title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-tan">{s.tagline}</p>
                  <p className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-faint transition-colors duration-300 group-hover:text-amberdeep" data-testid={`diensten-card-link-${i}`}>
                    {o.cardCta}
                    <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </p>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 rounded-[12px] border border-edge/50 bg-[#F7F4EC] p-6 md:p-8" data-testid="diensten-extra">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">{o.extraTitle}</p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/85">{o.extraText}</p>
            <button
              onClick={() => navigate("/", { state: { scrollTo: "#gesprek" } })}
              className="group mt-5 inline-flex items-center gap-2 rounded-[10px] bg-amber px-6 py-3 font-archivo text-sm font-bold text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
              data-testid="diensten-extra-cta"
            >
              {o.extraCta}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </div>
        </Reveal>
      </main>

      <footer className="border-t border-edge/40 py-8">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-faint" data-testid="diensten-footer">
          © 2026 4thscenery · Beyond The Ordinary
        </p>
      </footer>
    </div>
  );
}
